// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';
import type {
  PolicyStructuredDetails,
  PolicyCustomMetadata,
  PolicyMetadataEditHistory,
  PolicyMetadataExistsResponse,
  SaveStructuredDetailsRequest,
  AddCustomMetadataRequest,
  UpdateCustomMetadataRequest
} from '../types/policyMetadata';

/**
 * API module for policy metadata operations.
 * Corresponds to PolicyMetadataController endpoints.
 */
export const policyMetadataApi = {
  // =========================================================================
  // Structured Details
  // =========================================================================

  /**
   * Get structured policy details
   */
  getStructuredDetails: async (policyId: string): Promise<PolicyStructuredDetails> => {
    const response = await api.get<PolicyStructuredDetails>(
      `/policies/${policyId}/metadata/structured`
    );
    return response.data;
  },

  /**
   * Save structured policy details
   */
  saveStructuredDetails: async (
    policyId: string,
    request: SaveStructuredDetailsRequest
  ): Promise<PolicyStructuredDetails> => {
    const response = await api.put<PolicyStructuredDetails>(
      `/policies/${policyId}/metadata/structured`,
      request
    );
    return response.data;
  },

  /**
   * Save structured details on behalf of advisor (support staff only)
   */
  saveStructuredDetailsOnBehalf: async (
    policyId: string,
    advisorId: string,
    request: SaveStructuredDetailsRequest
  ): Promise<PolicyStructuredDetails> => {
    const response = await api.put<PolicyStructuredDetails>(
      `/policies/${policyId}/metadata/structured/on-behalf-of/${advisorId}`,
      request
    );
    return response.data;
  },

  // =========================================================================
  // Custom Metadata
  // =========================================================================

  /**
   * Get all custom metadata entries for a policy
   */
  getCustomMetadata: async (policyId: string): Promise<PolicyCustomMetadata[]> => {
    const response = await api.get<PolicyCustomMetadata[]>(
      `/policies/${policyId}/metadata/custom`
    );
    return response.data;
  },

  /**
   * Add custom metadata entry
   */
  addCustomMetadata: async (
    policyId: string,
    request: AddCustomMetadataRequest
  ): Promise<PolicyCustomMetadata> => {
    const response = await api.post<PolicyCustomMetadata>(
      `/policies/${policyId}/metadata/custom`,
      request
    );
    return response.data;
  },

  /**
   * Add custom metadata on behalf of advisor (support staff only)
   */
  addCustomMetadataOnBehalf: async (
    policyId: string,
    advisorId: string,
    request: AddCustomMetadataRequest
  ): Promise<PolicyCustomMetadata> => {
    const response = await api.post<PolicyCustomMetadata>(
      `/policies/${policyId}/metadata/custom/on-behalf-of/${advisorId}`,
      request
    );
    return response.data;
  },

  /**
   * Update custom metadata entry
   */
  updateCustomMetadata: async (
    policyId: string,
    metadataId: string,
    request: UpdateCustomMetadataRequest
  ): Promise<PolicyCustomMetadata> => {
    const response = await api.put<PolicyCustomMetadata>(
      `/policies/${policyId}/metadata/custom/${metadataId}`,
      request
    );
    return response.data;
  },

  /**
   * Update custom metadata on behalf of advisor (support staff only)
   */
  updateCustomMetadataOnBehalf: async (
    policyId: string,
    metadataId: string,
    advisorId: string,
    request: UpdateCustomMetadataRequest
  ): Promise<PolicyCustomMetadata> => {
    const response = await api.put<PolicyCustomMetadata>(
      `/policies/${policyId}/metadata/custom/${metadataId}/on-behalf-of/${advisorId}`,
      request
    );
    return response.data;
  },

  /**
   * Delete custom metadata entry
   */
  deleteCustomMetadata: async (
    policyId: string,
    metadataId: string,
    changeReason: string
  ): Promise<void> => {
    await api.delete(`/policies/${policyId}/metadata/custom/${metadataId}`, {
      params: { changeReason }
    });
  },

  /**
   * Delete custom metadata on behalf of advisor (support staff only)
   */
  deleteCustomMetadataOnBehalf: async (
    policyId: string,
    metadataId: string,
    advisorId: string,
    changeReason: string
  ): Promise<void> => {
    await api.delete(
      `/policies/${policyId}/metadata/custom/${metadataId}/on-behalf-of/${advisorId}`,
      { params: { changeReason } }
    );
  },

  // =========================================================================
  // Edit History
  // =========================================================================

  /**
   * Get edit history for policy metadata
   */
  getEditHistory: async (
    policyId: string,
    options?: {
      fieldFilter?: string;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<PolicyMetadataEditHistory[]> => {
    const response = await api.get<PolicyMetadataEditHistory[]>(
      `/policies/${policyId}/metadata/history`,
      { params: options }
    );
    return response.data;
  },

  // =========================================================================
  // Metadata Existence Check
  // =========================================================================

  /**
   * Check if policy has any metadata
   */
  checkMetadataExists: async (policyId: string): Promise<PolicyMetadataExistsResponse> => {
    const response = await api.get<PolicyMetadataExistsResponse>(
      `/policies/${policyId}/metadata/exists`
    );
    return response.data;
  },

  // =========================================================================
  // Version Association
  // =========================================================================

  /**
   * Associate metadata with a document version
   */
  associateWithDocumentVersion: async (
    policyId: string,
    documentVersion: number,
    changeReason: string
  ): Promise<{ policyId: string; documentVersion: number; associatedAt: string; associatedBy: string }> => {
    const response = await api.put(
      `/policies/${policyId}/metadata/version-association`,
      { documentVersion, changeReason }
    );
    return response.data;
  }
};
